/**
 * 閲覧パスワードの入力面（EP-5 password ポリシー）。
 *
 * マニフェストが 403 `E_PASSWORD_REQUIRED` を返したときだけ表示し、解錠に成功したら
 * 消えて通常の読み込みへ戻る。認可の実体はサーバ側（解錠は署名付き Cookie）であり、
 * この画面は入力の受け口にすぎない。
 */
import { cls, uniqueId } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import { ViewerError } from '../core/errors';
import type { Translate } from '../i18n/i18n';

/**
 * マニフェスト URL から解錠エンドポイントを導く（`…/books/{slug}/manifest` → `…/unlock`）。
 * 想定外の形の URL では null を返し、呼び出し側は元の 403 のまま失敗させる。
 */
export function unlockUrlFrom(source: string): string | null {
  const [path, query] = source.split('?');
  if (!path || !path.endsWith('/manifest')) return null;
  return path.slice(0, -'/manifest'.length) + '/unlock' + (query ? '?' + query : '');
}

export class UnlockForm {
  private readonly el: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private readonly error: HTMLParagraphElement;
  private readonly titleId = uniqueId('unlock-title');
  private settle: { resolve: () => void; reject: (reason: unknown) => void } | null = null;
  /** 入力面を出したときに焦点があった要素（畳んだら戻す — FR-A11Y-01）。 */
  private restoreFocus: HTMLElement | null = null;

  constructor(
    private readonly doc: Document,
    private readonly root: HTMLElement,
    registry: DisposeRegistry,
    private readonly translate: Translate,
    private readonly url: string,
  ) {
    this.el = doc.createElement('form');
    this.el.className = cls('unlock');
    // 見出しを名前にする（M8c-2 手順 3）。**`role="dialog"` は付けない** — 理由は 2 つ。
    // (1) `<form>` に dialog は置けない（ARIA in HTML の許可役割の外）。
    // (2) この面には閉じる手段が無く（解錠が唯一の出口）、モーダルとして扱って焦点を
    //     閉じ込めるとキーボードトラップ（WCAG 2.1.2）そのものになる。埋め込み先の
    //     ページもまだ生きているので、周囲を無いことにするのも実態と合わない。
    // 名前つきの form は landmark として拾われ、支援技術には「何の入力面か」が伝わる。
    this.el.setAttribute('aria-labelledby', this.titleId);

    const title = doc.createElement('h2');
    title.className = cls('unlock-title');
    title.id = this.titleId;
    title.textContent = translate('unlock.title');

    const description = doc.createElement('p');
    description.className = cls('unlock-text');
    description.textContent = translate('unlock.description');

    const label = doc.createElement('label');
    label.className = cls('unlock-label');
    label.textContent = translate('unlock.password');

    this.input = doc.createElement('input');
    this.input.type = 'password';
    this.input.className = cls('unlock-input');
    // パスワードマネージャに入力させる（WCAG 2.2 の 3.3.8・M10 手順 4）。
    // 解錠は「覚えているパスワードを打つ」認知機能テストなので、注記 2 が挙げる救済手段
    // （マネージャの自動入力・貼り付け）を塞がないことが適合の条件になる。従来の
    // `autocomplete="off"` は自動入力を明示的に拒む宣言だったため取り下げた。
    // `name` はマネージャ側の推定を助けるために付ける（送信は JSON なので値は使わない）。
    this.input.autocomplete = 'current-password';
    this.input.name = 'password';
    label.appendChild(this.input);

    const submit = doc.createElement('button');
    submit.type = 'submit';
    submit.className = `${cls('button')} ${cls('unlock-submit')}`;
    submit.textContent = translate('unlock.submit');

    this.error = doc.createElement('p');
    this.error.className = cls('unlock-error');
    this.error.setAttribute('role', 'alert');
    this.error.hidden = true;

    this.el.append(title, description, label, submit, this.error);
    registry.listen(this.el, 'submit', (event) => {
      event.preventDefault();
      void this.send();
    });
  }

  /** 入力面を出し、解錠に成功するまで待つ。 */
  wait(): Promise<void> {
    this.restoreFocus = this.doc.activeElement as HTMLElement | null;
    this.root.appendChild(this.el);
    this.input.focus();
    return new Promise<void>((resolve, reject) => {
      this.settle = { resolve, reject };
    });
  }

  /** 画面から取り除く。待機中なら中断として終わらせる。 */
  close(): void {
    this.dismiss();
    const settle = this.settle;
    this.settle = null;
    settle?.reject(new DOMException('解錠を中断しました', 'AbortError'));
  }

  /** 入力面を畳んでフォーカスを開く前の場所へ戻す。 */
  private dismiss(): void {
    this.el.remove();
    const restore = this.restoreFocus;
    this.restoreFocus = null;
    restore?.focus();
  }

  private async send(): Promise<void> {
    const password = this.input.value;
    if (!password) return;
    this.error.hidden = true;

    let response: Response;
    try {
      response = await fetch(this.url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
    } catch (cause) {
      this.showError(this.translate('unlock.failed'));
      void cause;
      return;
    }

    if (!response.ok) {
      this.showError(this.translate(response.status === 403 ? 'unlock.invalid' : 'unlock.failed'));
      return;
    }

    const settle = this.settle;
    this.settle = null;
    this.dismiss();
    settle?.resolve();
  }

  private showError(message: string): void {
    this.error.textContent = message;
    this.error.hidden = false;
    this.input.select();
  }
}

/** パスワード要求のエラーか（サーバのコードをそのまま判定に使う）。 */
export function isPasswordRequired(error: unknown): boolean {
  return error instanceof ViewerError && error.code === 'E_PASSWORD_REQUIRED';
}
